import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { StockCategoryFilter } from '../types/stockCategories';
import { useAuth } from './useAuth';

interface PlayData {
  won: boolean;
  guesses: number;
  timeMs: number;
  submittedAt: string;
}

interface CheckGamePlayedResult {
  hasPlayed: boolean;
  playData: PlayData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseCheckGamePlayedOptions {
  category: StockCategoryFilter;
  gameDate: string;
  enabled?: boolean;
}

/**
 * Hook to check if the current AUTHENTICATED USER has already played a specific category today.
 * Prevents cross-device replay for logged-in users by verifying against Supabase.
 *
 * IMPORTANT: Only checks for authenticated users. Anonymous users rely on localStorage.
 *
 * @param options - Configuration options
 * @returns Play status and data
 */
export const useCheckGamePlayed = ({
  category,
  gameDate,
  enabled = true
}: UseCheckGamePlayedOptions): CheckGamePlayedResult => {
  const { user } = useAuth();
  const [hasPlayed, setHasPlayed] = useState(false);
  const [playData, setPlayData] = useState<PlayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const checkPlayStatus = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    // ONLY check Supabase for logged-in users
    // Anonymous users rely on localStorage (existing behavior)
    const userId = user?.id || null;
    if (!userId) {
      setLoading(false);
      setHasPlayed(false);
      setPlayData(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Create abort controller for cleanup
    const ac = new AbortController();
    abortControllerRef.current = ac;

    try {
      // Call Supabase RPC with 15-second timeout using official API
      const { data, error: rpcError } = await supabase
        .rpc('check_game_played', {
          p_category: category,
          p_game_date: gameDate,
          p_user_id: userId,
          p_device_id: null // Always null - we don't track anonymous users via Supabase
        })
        .abortSignal(AbortSignal.timeout(15000)); // 15s timeout

      if (rpcError) {
        throw rpcError;
      }

      if (data && data.played) {
        setHasPlayed(true);
        setPlayData({
          won: data.won,
          guesses: data.guesses,
          timeMs: data.time_ms,
          submittedAt: data.submitted_at
        });
      } else {
        setHasPlayed(false);
        setPlayData(null);
      }
    } catch (err) {
      console.error('Failed to check game played status:', err);
      setError(err instanceof Error ? err.message : 'Failed to check play status');
      // On error, assume not played to allow game to proceed
      setHasPlayed(false);
      setPlayData(null);
    } finally {
      // Clear the abort controller ref if it's still our controller
      if (abortControllerRef.current === ac) {
        abortControllerRef.current = null;
      }
      setLoading(false);
    }
  }, [category, gameDate, user?.id, enabled]);

  // Check on mount and when dependencies change
  useEffect(() => {
    checkPlayStatus();
  }, [checkPlayStatus]);

  // Cleanup: abort pending requests on category/date/user change or unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [category, gameDate, user?.id]);

  return {
    hasPlayed,
    playData,
    loading,
    error,
    refetch: checkPlayStatus
  };
};
